import React, { useEffect, useState } from 'react';
import './AdminUserList.css';
import { adminApi } from './utils/adminApi';
import { formatDate } from './utils/format';
import { countryCodeToFlagEmoji } from './utils/countryFlag';
import { STATUS_COLORS, PLAN_LABELS } from './constants';
import RefreshButton from './RefreshButton';

const FILTER_OPTIONS = [
  { value: '', label: 'All users' },
  { value: 'status:active', label: 'Status: active' },
  { value: 'status:inactive', label: 'Status: inactive' },
  { value: 'planId:custom', label: 'Plan: custom' },
  { value: 'planId:pro', label: 'Plan: pro' },
  { value: 'planId:ultimate', label: 'Plan: ultimate' },
  { value: 'banned:true', label: 'Banned' },
  { value: 'suspended:true', label: 'Suspended' },
  { value: 'admin:true', label: 'Admins' },
];

const planStatusLabel = (status) => {
  switch (status) {
    case 'active': return 'Active';
    case 'expired': return 'Expired';
    case 'inactive': return 'Inactive';
    default: return 'None';
  }
};

const AdminUserList = ({ onSelectUser }) => {
  const [emailInput, setEmailInput] = useState('');
  const [activeSearchEmail, setActiveSearchEmail] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentPage = pages[pageIndex] || { users: [], nextCursor: null };

  const fetchPage = async (cursor, replacePages) => {
    setLoading(true);
    setError('');
    try {
      const [field, value] = filterValue ? filterValue.split(':') : [];
      const params = { limit: 25, cursor };
      if (field) params[field] = value;

      const res = await adminApi.listUsers(params);
      if (replacePages) {
        setPages([res]);
        setPageIndex(0);
      } else {
        setPages((prev) => [...prev, res]);
        setPageIndex((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Error listing users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setActiveSearchEmail('');
    fetchPage(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const runSearch = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.listUsers({ email: emailInput.trim() });
      setActiveSearchEmail(emailInput.trim());
      setPages([res]);
      setPageIndex(0);
    } catch (err) {
      console.error('Error searching users:', err);
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setEmailInput('');
    setActiveSearchEmail('');
    fetchPage(undefined, true);
  };

  const renderStatusBadges = (user) => (
    <div className="admin-row-badges">
      <span className="admin-badge" style={{ color: STATUS_COLORS[user.planStatus] }}>
        {planStatusLabel(user.planStatus)}
      </span>
      {user.admin && <span className="admin-badge" style={{ color: STATUS_COLORS.admin }}>Admin</span>}
      {user.accountBanned && <span className="admin-badge" style={{ color: STATUS_COLORS.banned }}>Banned</span>}
      {user.accountSuspended && <span className="admin-badge" style={{ color: STATUS_COLORS.suspended }}>Suspended</span>}
    </div>
  );

  return (
    <div>
      <div className="admin-user-controls">
        <form className="admin-search-form" onSubmit={runSearch}>
          <input
            type="email"
            placeholder="Search by exact email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          <button type="submit" className="admin-btn admin-btn-secondary">Search</button>
          {activeSearchEmail && (
            <button type="button" className="admin-btn admin-btn-secondary" onClick={clearSearch}>Clear</button>
          )}
        </form>

        <select
          className="admin-filter-select"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          disabled={Boolean(activeSearchEmail)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <RefreshButton
          onRefresh={() => (activeSearchEmail ? runSearch({ preventDefault: () => {} }) : fetchPage(undefined, true))}
          title="Refresh user list"
        />
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : currentPage.users.length === 0 ? (
        <div className="no-data">No users found</div>
      ) : (
        <div className="admin-user-rows">
          {currentPage.users.map((user) => (
            <div key={user.id} className="admin-user-row" onClick={() => onSelectUser(user.id)}>
              <div className="admin-user-row-main">
                <span className="admin-user-email">{user.email || '(no email)'}</span>
                <span className="admin-user-meta">
                  {user.location?.countryCode ? `${countryCodeToFlagEmoji(user.location.countryCode)} ${user.location.country}` : '—'}
                  {' · '}
                  {user.planId ? (PLAN_LABELS[user.planId] || user.planId) : 'No plan'}
                  {' · '}
                  Joined {formatDate(user.createdAt)}
                </span>
              </div>
              {renderStatusBadges(user)}
            </div>
          ))}
        </div>
      )}

      {!activeSearchEmail && (
        <div className="admin-pagination">
          <button
            className="admin-btn admin-btn-secondary"
            disabled={pageIndex === 0 || loading}
            onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
          >
            Previous
          </button>
          <span className="admin-page-indicator">Page {pageIndex + 1}</span>
          <button
            className="admin-btn admin-btn-secondary"
            disabled={loading || !currentPage.nextCursor}
            onClick={() => {
              if (pages[pageIndex + 1]) {
                setPageIndex((prev) => prev + 1);
              } else {
                fetchPage(currentPage.nextCursor, false);
              }
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUserList;
